import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const statusStyle = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  confirmed: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  shipped: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success(`Order marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("ats_token");
      const url = `${BACKEND_URL}/api/admin/orders/export`;
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "orders.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-amber-400">Fulfilment</div>
          <h1 className="font-serif-display text-3xl mt-2">Orders</h1>
        </div>
        <Button onClick={exportCsv} data-testid="export-orders-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-slate-800/60 border border-white/5 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-slate-400">Order</TableHead>
              <TableHead className="text-slate-400">Customer</TableHead>
              <TableHead className="text-slate-400">Items</TableHead>
              <TableHead className="text-slate-400">Total</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 text-right">Update</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10"
                data-testid="orders-empty">No orders yet.</TableCell></TableRow>
            ) : orders.map((o, i) => (
              <TableRow key={o.id} className="border-white/5" data-testid={`order-row-${i}`}>
                <TableCell>
                  <div className="font-mono-accent text-xs">#{String(o.id).slice(-8)}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {new Date(o.created_at).toLocaleDateString("en-IN")}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{o.customer_name}</div>
                  <div className="text-xs text-slate-500">{o.customer_phone}</div>
                </TableCell>
                <TableCell className="text-xs text-slate-400">
                  {(o.items || []).map(it => `${it.name} ×${it.quantity}`).join(", ")}
                </TableCell>
                <TableCell className="font-mono-accent">
                  ₹{Number(o.total).toLocaleString("en-IN")}
                </TableCell>
                <TableCell>
                  <Badge className={statusStyle[o.status] || ""}>{o.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="w-36 h-9 bg-slate-900/50 border-white/10"
                      data-testid={`order-status-select-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
